import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from 'context/AuthContext';
import { useServerTime } from 'hooks/useServerTime';
import {
  fetchFeedbackExperienceCatalog,
  fetchQuestionExperienceCatalog,
  getDeadLineExperienceCatalog,
  getRegulationExperienceCatalog,
  loadExperiencePurchase,
  registerFeedbackExperienceCatalog,
  undoExperiencePurchase,
} from 'services/experienceCataolog.service';
import { getNamedDates } from 'services/utils';
import { ExperienceCatalogQuestions } from 'types/experienceCatalog';
import { readCatalogPeriod } from 'utils/catalog-period';
import { canUndoPurchase } from 'utils/experience-catalog';

import { formatarData } from '../catalogo-modelo';
import AvaliacaoDoCatalogo, { RespostaDaAvaliacao } from '../components/AvaliacaoDoCatalogo';
import AvisoDeImpedimento from '../components/AvisoDeImpedimento';
import CabecalhoDoCatalogo from '../components/CabecalhoDoCatalogo';
import ErroDeCarga from '../components/ErroDeCarga';
import { CancelamentoRealizado, PrazoEncerrado } from '../components/EstadoDaEscolha';
import ExperienciaConfirmada from '../components/ExperienciaConfirmada';
import ModalDeCancelamento from '../components/ModalDeCancelamento';
import ModalDeRegras from '../components/ModalDeRegras';
import { useCargaPorChave } from 'hooks/useCargaPorChave';
import { CicloComInicio, useCicloDoCatalogo } from '../hooks/useCicloDoCatalogo';
import { ExperienciaEscolhida, montarExperienciaEscolhida, nomeDoSemestre } from './experiencia-escolhida';

interface DadosDaEscolha {
  experiencia: ExperienciaEscolhida | null;
  regulamentoUrl: string;
  perguntas?: ExperienceCatalogQuestions[];
  jaAvaliou: boolean;
  fim?: Date;
}

/** Resgate, regulamento, perguntas, avaliação já enviada e prazo do ciclo — as mesmas leituras de antes. */
async function carregarEscolha(chave: string, ciclo: CicloComInicio): Promise<DadosDaEscolha> {
  const [resgate, regulamento, perguntas, avaliacoes, periodo] = await Promise.all([
    loadExperiencePurchase(chave, ciclo.title),
    getRegulationExperienceCatalog(ciclo.title),
    fetchQuestionExperienceCatalog(),
    fetchFeedbackExperienceCatalog(chave, ciclo.title),
    getDeadLineExperienceCatalog(ciclo._id),
  ]);
  return {
    experiencia: montarExperienciaEscolhida(resgate),
    regulamentoUrl: regulamento?.file?.url || '',
    perguntas,
    jaAvaliou: Array.isArray(avaliacoes) && avaliacoes.length > 0,
    // O prazo sai de `endDate`, como na vitrine; sem data legível não há prazo (nada de "agora").
    fim: readCatalogPeriod(periodo).end,
  };
}

/** Mensagem do erro de cancelamento: a do servidor, já limpa pelo serviço, ou a padrão. */
const mensagemDoErro = (erro: unknown) => (erro instanceof Error && erro.message) || 'Não foi possível cancelar o resgate. Tente novamente.';

export default function ExperienceCatalogFeedbackPage() {
  const { userInfo } = useAuth();
  const navigate = useNavigate();
  const verCatalogo = () => navigate('/catalogo-de-experiencias-list');
  const { ciclos, cicloId, ciclo, trocarCiclo } = useCicloDoCatalogo();
  // Relógio do SERVIDOR: decide se o prazo de cancelamento passou.
  const { realDatetime: agora, hasTime: temHoraDoServidor } = useServerTime({ tick: true });

  const [regrasAbertas, setRegrasAbertas] = useState(false);
  const [cancelamentoAberto, setCancelamentoAberto] = useState(false);
  const [cancelando, setCancelando] = useState(false);
  const [erroDoCancelamento, setErroDoCancelamento] = useState<string>();
  const [cancelado, setCancelado] = useState(false);
  const [avaliacaoEnviada, setAvaliacaoEnviada] = useState(false);

  const { carga, recarregar } = useCargaPorChave<DadosDaEscolha>(
    userInfo?.chave && ciclo ? `${ciclo._id}|${userInfo.chave}` : undefined,
    () => (userInfo?.chave && ciclo ? carregarEscolha(userInfo.chave, ciclo) : Promise.reject(new Error('Sem jogador ou ciclo para carregar.'))),
  );

  // Outro ciclo, outra história: a confirmação do cancelamento e o agradecimento não o acompanham.
  useEffect(() => {
    setCancelado(false);
    setAvaliacaoEnviada(false);
  }, [cicloId]);

  const dados = carga.estado === 'pronto' ? carga.dados : undefined;
  const experiencia = dados?.experiencia ?? null;
  const fim = dados?.fim;

  // Com o relógio do servidor: um navegador adiantado mostraria "expirado" para quem ainda pode cancelar.
  const encerrado = temHoraDoServidor && !!fim && agora > fim.getTime();
  const podeCancelar = temHoraDoServidor && canUndoPurchase(!!experiencia, agora, fim);
  const prazoExpirado = !!experiencia && encerrado;
  const prazo = fim ? formatarData(fim) : '';

  // Sem resgate e com o prazo aberto, a vitrine é o lugar do jogador. `!cancelado` protege a
  // confirmação do cancelamento, que é justamente o estado "sem resgate ativo" (DESF-01.2).
  useEffect(() => {
    if (dados && temHoraDoServidor && !experiencia && !encerrado && !cancelado) navigate('/catalogo-de-experiencias-list');
  }, [dados, temHoraDoServidor, experiencia, encerrado, cancelado, navigate]);

  const cancelar = async () => {
    if (!ciclo?.title) return;
    setCancelando(true);
    setErroDoCancelamento(undefined);
    try {
      await undoExperiencePurchase(ciclo.title);
      setCancelamentoAberto(false);
      setCancelado(true);
    } catch (erro) {
      setErroDoCancelamento(mensagemDoErro(erro));
    } finally {
      setCancelando(false);
    }
  };

  // A falha volta para o formulário, que mostra o erro e libera um novo envio.
  const enviarAvaliacao = async ({ notas, comentario, identificar }: RespostaDaAvaliacao) => {
    if (!userInfo?.chave) return;
    const respostas = Object.entries(notas).map(([indice, nota]) => {
      const pergunta = dados?.perguntas?.[Number(indice)];
      return { questionId: pergunta?._id, rating: nota, pergunta: pergunta?.pergunta };
    });
    await registerFeedbackExperienceCatalog(userInfo.chave, comentario, respostas, identificar, ciclo?.title);
    setAvaliacaoEnviada(true);
  };

  return (
    <div className="mx-auto max-w-[1000px] px-4 pb-6 leading-[normal] lg:px-0">
      <CabecalhoDoCatalogo ciclos={ciclos} cicloId={cicloId} onCiclo={trocarCiclo} />

      {(ciclo?.date_start ?? 0) >= getNamedDates().ciclo_2S2025 && <AvisoDeImpedimento />}

      {carga.estado === 'carregando' && (
        <div aria-busy="true" className="flex flex-col gap-5">
          <p className="sr-only" role="status">
            Carregando a sua experiência
          </p>
          <div className="h-[360px] animate-pulse rounded-xl bg-surface-200 motion-reduce:animate-none" />
        </div>
      )}

      {carga.estado === 'erro' && <ErroDeCarga mensagem="Não foi possível carregar a sua experiência." onTentarDeNovo={recarregar} />}

      {dados && (
        <>
          {cancelado ? (
            <CancelamentoRealizado prazo={prazo} onVerCatalogo={verCatalogo} />
          ) : experiencia ? (
            <ExperienciaConfirmada
              experiencia={experiencia}
              regulamentoUrl={dados.regulamentoUrl}
              prazo={prazo}
              prazoExpirado={prazoExpirado}
              podeCancelar={podeCancelar}
              onRegras={() => setRegrasAbertas(true)}
              onVerCatalogo={verCatalogo}
              onCancelar={() => {
                setErroDoCancelamento(undefined);
                setCancelamentoAberto(true);
              }}
            />
          ) : (
            encerrado && <PrazoEncerrado semestre={nomeDoSemestre(ciclo?.title)} onVerCatalogo={verCatalogo} />
          )}

          {!encerrado && <AvaliacaoDoCatalogo perguntas={dados.perguntas} jaAvaliou={dados.jaAvaliou || avaliacaoEnviada} onEnviar={enviarAvaliacao} />}

          {experiencia && (
            <>
              <ModalDeRegras isOpen={regrasAbertas} escolha={experiencia.escolha} regras={experiencia.regras} onFechar={() => setRegrasAbertas(false)} />
              <ModalDeCancelamento
                isOpen={cancelamentoAberto}
                nome={experiencia.nome}
                prazo={prazo}
                cancelando={cancelando}
                erro={erroDoCancelamento}
                onConfirmar={cancelar}
                onFechar={() => setCancelamentoAberto(false)}
              />
            </>
          )}
        </>
      )}
    </div>
  );
}
